import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import '../styles/design-system.css';
import './HomeContent.css';
import CountUp from 'react-countup';
import SakuraParticles from './SakuraParticles';

const HomeContent = () => {
    const { t } = useTranslation();

    const stats = [
        { label: 'Coaching Hours', value: 1000, suffix: '+', icon: 'fas fa-university' },
        { label: 'Recommendations', value: 1500, suffix: '+', icon: 'fas fa-graduation-cap' },
        { label: 'Success Stories', value: 700, suffix: '+', icon: 'fas fa-users' },
    ];

    const affiliates = [
        {
            name: 'Sun Lanka Vocational',
            desc: 'Dedicated to excellence in language education and skill development.',
            img: './images/homes/sun.jpg'
        },
        {
            name: 'Kyoudai Lanka International',
            desc: 'Empowering individuals with skills to excel in the global workforce.',
            img: './images/homes/kyo.jpg'
        }
    ];

    return (
        <div className="home-content-section" style={{ position: 'relative' }}>

            {/* Stats Section */}
            <div className="container" style={{ padding: '60px 20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '30px' }}>
                    {stats.map((stat, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="stat-card-3d"
                        >
                            <div style={{ fontSize: '2.5rem', color: '#eee', marginBottom: '-30px' }}>
                                <i className={stat.icon}></i>
                            </div>
                            <h3 style={{ fontSize: '2.8rem', fontWeight: '700', color: 'var(--color-primary-red)', marginBottom: '5px', position: 'relative' }}>
                                <CountUp end={stat.value} duration={2.5} enableScrollSpy scrollSpyOnce />{stat.suffix}
                            </h3>
                            <p style={{ fontSize: '1rem', fontWeight: '500', color: '#666', textTransform: 'uppercase', letterSpacing: '1px' }}>{stat.label}</p>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Affiliates Section - Compact */}
            <div className="affiliates-section">
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                        <h2 className="stylish-header centered">{t("home2") || "Our Partners"}</h2>
                        <p className="text-body" style={{ maxWidth: '600px', margin: '0 auto', fontSize: '0.95rem' }}>Building bridges between Sri Lanka and Japan through trusted partnerships.</p>
                    </div>
                    <div className="affiliates-grid">
                        {affiliates.map((company, i) => (
                            <motion.div
                                key={i}
                                whileHover={{ y: -5 }}
                                className="glass-card-hover affiliate-card"
                            >
                                <div className="affiliate-img-wrapper">
                                    <img src={company.img} alt={company.name} className="affiliate-img" />
                                </div>
                                <h3 className="affiliate-title">{company.name}</h3>
                                <p className="affiliate-desc">{company.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Vision & Mission - Sticky Tree Layout */}
            <div className="vision-mission-section">
                <div className="container" style={{ maxWidth: '1400px' }}>
                    <div className="vision-mission-layout">

                        {/* Left Side - Sticky Tree */}
                        <div className="sticky-tree-wrapper">
                            <div className="tree-particles-container">
                                <SakuraParticles count={30} />
                            </div>
                            <motion.img
                                src="./images/homes/tree.png"
                                alt="Tree of Growth"
                                initial={{ opacity: 0, scale: 0.9, x: -50 }}
                                whileInView={{ opacity: 1, scale: 1.2, x: 0 }}
                                transition={{ duration: 1 }}
                                className="tree-image"
                            />
                        </div>

                        {/* Right Side - Content */}
                        <div className="vision-mission-content">
                            {/* Mission */}
                            <div className="vision-mission-split">
                                <motion.div
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.8 }}
                                    viewport={{ once: true }}
                                >
                                    <span className="section-tag">Our Purpose</span>
                                    <h2 className="stylish-header">Mission</h2>
                                    <p className="text-body" style={{ fontSize: '1rem', marginBottom: '20px', color: '#444', lineHeight: '1.6' }}>
                                        {t("home6") || "To provide high-quality, industry-relevant vocational education and training grounded in Japanese values..."}
                                    </p>
                                    <button className="btn-secondary" style={{ padding: '8px 25px', fontSize: '0.9rem', width: 'auto' }}>Learn More</button>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.8 }}
                                    viewport={{ once: true }}
                                    style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}
                                >
                                    <div style={{ position: 'absolute', top: '-10px', right: '10%', width: '60%', height: '100%', background: '#ff9e99', borderRadius: '50%', opacity: 0.1, zIndex: 0, filter: 'blur(20px)' }}></div>
                                    <img src="./images/homes/mission.gif" alt="Mission" style={{ width: '50%', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', position: 'relative', zIndex: 1 }} />
                                </motion.div>
                            </div>

                            {/* Vision */}
                            <div className="vision-mission-split">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.8 }}
                                    viewport={{ once: true }}
                                    style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}
                                >
                                    <div style={{ position: 'absolute', bottom: '-10px', left: '10%', width: '60%', height: '100%', background: '#ff9e99', borderRadius: '50%', opacity: 0.1, zIndex: 0, filter: 'blur(20px)' }}></div>
                                    <img src="./images/homes/vision.gif" alt="Vision" style={{ width: '50%', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', position: 'relative', zIndex: 1 }} />
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, x: 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.8 }}
                                    viewport={{ once: true }}
                                >
                                    <span className="section-tag">Future Outlook</span>
                                    <h2 className="stylish-header">Vision</h2>
                                    <p className="text-body" style={{ fontSize: '1rem', marginBottom: '20px', color: '#444', lineHeight: '1.6' }}>
                                        {t("home4") || "Empowering individuals with the skills, discipline, and innovation to excel in their chosen vocations..."}
                                    </p>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomeContent;
